import { useEffect, useState } from "react";
import { Bell, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  getApiErrorDetail,
  listUnreadNotifications,
  markNotificationRead,
  type NotificationOut,
} from "@/lib/api";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";
const TEXT_DIM = "oklch(0.85 0.01 243)";

// Drawer de notifications. Polling "opportuniste" toutes les 60s ;
// au focus du drawer on declenche un reload immediat.
//
// Robustesse : l'etat `items` reste typed `NotificationOut[] | null`
// (null = en cours de chargement initial), mais TOUS les acces utilisent
// `Array.isArray(items)` avant `.map / .length / .filter`. C'est de la
// defense en profondeur : `listUnreadNotifications` cote lib/api est
// deja blinde par `ensureArray`, mais si quoique ce soit injecte une
// valeur non-array via `setItems` (regression future, devtools, etc.),
// le rendu ne crashe plus.
export function NotificationsDrawer() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await listUnreadNotifications();
      // Garde-fou : `ensureArray` cote api.ts garantit deja un tableau,
      // mais on revalidera ici pour resister a toute regression de typage.
      setItems(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(
        getApiErrorDetail(err) ?? "Echec du chargement des notifications.",
      );
    }
  };

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open]);

  const onMarkRead = async (id: number) => {
    try {
      await markNotificationRead(id);
      setItems((prev) =>
        Array.isArray(prev) ? prev.filter((n) => n.id !== id) : prev,
      );
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Echec marquage lu.");
    }
  };

  const safeItems: NotificationOut[] = Array.isArray(items) ? items : [];
  const unreadCount = safeItems.length;
  const loading = items === null && !error;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          style={{ color: TEXT_DIM }}
        >
          {unreadCount > 0 ? (
            <BellRing size={16} style={{ color: GOLD }} />
          ) : (
            <Bell size={16} />
          )}
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 text-[10px] font-bold rounded-full px-1.5 py-0.5"
              style={{ background: GOLD, color: MARINE_DEEP }}
            >
              {unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        style={{
          background: MARINE_DEEP,
          color: "oklch(1 0 0)",
          borderLeft: `1px solid color-mix(in oklch, ${GOLD} 25%, transparent)`,
        }}
      >
        <SheetHeader>
          <SheetTitle
            style={{
              color: "oklch(1 0 0)",
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "1.5rem",
            }}
          >
            Notifications
          </SheetTitle>
          <SheetDescription style={{ color: TEXT_DIM }}>
            {unreadCount === 0
              ? "Aucune notification non-lue."
              : `${unreadCount} non-lue${unreadCount > 1 ? "s" : ""}.`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin" size={20} />
            </div>
          )}
          {error && (
            <p className="text-sm" style={{ color: "rgb(254 202 202)" }}>
              {error}
            </p>
          )}
          {safeItems.map((n) => (
            <article
              key={n.id}
              className="rounded-sm border p-3 text-sm"
              style={{
                borderColor: `color-mix(in oklch, ${GOLD} 18%, transparent)`,
                background: "color-mix(in oklch, oklch(1 0 0) 2%, transparent)",
              }}
            >
              <header className="flex items-start justify-between gap-2">
                <h3
                  className="font-medium"
                  style={{ color: "oklch(1 0 0)" }}
                >
                  {n.title}
                </h3>
                <button
                  onClick={() => void onMarkRead(n.id)}
                  className="text-[10px] uppercase tracking-widest"
                  style={{ color: GOLD, letterSpacing: "0.12em" }}
                >
                  marquer lu
                </button>
              </header>
              <p className="mt-1" style={{ color: TEXT_DIM, lineHeight: 1.5 }}>
                {n.message}
              </p>
              <footer
                className="mt-2 text-[10px]"
                style={{ color: "oklch(0.65 0.01 243)" }}
              >
                {n.reference_dossier} ·{" "}
                {new Date(n.created_at).toLocaleString("fr-FR")}
              </footer>
            </article>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
